import { type FC, type ReactNode, useCallback, useState } from 'react'
import styled from 'styled-components'

import CaretDownIcon from 'assets/svg/app/caret-down.svg'
import { Input } from 'components/Input/Input'
import { Body } from 'components/Text'
import { FlexDivCol, FlexDivRowCentered } from 'components/layout/flex'
import useClickOutside from 'hooks/useClickOutside'

interface DropdownOption {
	value: string
	label: string
	icon: ReactNode
}

interface DropdownFieldProps {
	label: string
	options: DropdownOption[]
	value: string
	onChange: (value: string) => void
}

const DropdownField: FC<DropdownFieldProps> = ({ label, options, value, onChange }) => {
	const [open, setOpen] = useState(false)
	const { ref } = useClickOutside(() => setOpen(false))
	const selectedOption = options.find((option) => option.value === value)
	const handleToggle = useCallback(() => setOpen(!open), [open])
	const handleOptionClick = useCallback(
		(optionValue: string) => {
			onChange(optionValue)
			setOpen(false)
		},
		[onChange]
	)

	return (
		<FlexDivCol rowGap="10px" ref={ref}>
			<Body color="secondary">{label}</Body>
			<DropdownContainer>
				<InputContainer onClick={handleToggle}>
					<StyledInput readOnly />
					<FlexDivRowCentered justifyContent="space-between">
						<FieldContent justifyContent="flex-start" columnGap="10px">
							{selectedOption?.icon}
							<DropItemLabel color="primary" weight="semiBold" size="large">
								{selectedOption?.label}
							</DropItemLabel>
						</FieldContent>
						<StyledCaretDownIcon />
					</FlexDivRowCentered>
				</InputContainer>
				{open && (
					<DropdownList>
						{options.map((option) => (
							<DropdownItem key={option.value} onClick={() => handleOptionClick(option.value)}>
								<FlexDivRowCentered justifyContent="flex-start" columnGap="10px">
									{option.icon}
									<DropItemLabel color="primary" weight="semiBold" size="large">
										{option.label}
									</DropItemLabel>
								</FlexDivRowCentered>
							</DropdownItem>
						))}
					</DropdownList>
				)}
			</DropdownContainer>
		</FlexDivCol>
	)
}

const DropdownContainer = styled.div`
	position: relative;
	pointer: cursor;
`

const FieldContent = styled(FlexDivRowCentered)`
	position: absolute;
	left: 15px;
	top: 13px;
`

const StyledCaretDownIcon = styled(CaretDownIcon)`
	path {
		stroke-width: 0.5;
		color: ${(props) => props.theme.colors.selectedTheme.button.text.primary};
	}
	position: absolute;
	right: 15px;
	top: 15px;
`

const InputContainer = styled.div`
	position: relative;
	cursor: pointer;
`

const StyledInput = styled(Input)`
	width: 100%;
`

const DropdownList = styled.ul`
	position: absolute;
	top: calc(100% + 2px);
	left: 0;
	right: 0;
	background: ${(props) => props.theme.colors.selectedTheme.input.background};
	border: ${(props) => props.theme.colors.selectedTheme.input.border};
	border-radius: 8px;
	list-style: none;
	overflow-y: auto;
	z-index: 10;
`

const DropdownItem = styled.li`
	padding: 10px 15px;
	cursor: pointer;
	&:hover {
		background: ${(props) => props.theme.colors.selectedTheme.newTheme.dropdown.hover.background};
	}
`

const DropItemLabel = styled(Body)`
	margin-top: 2px;
`

export default DropdownField
